import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotificationContext } from '../contexts/NotificationProvider';
import { FCM_WEB_PUSH_ENABLED } from '../config/fcmWebPushEnabled';

/**
 * Header bell — notifications list, plus push setup when alerts are not connected.
 */
const HeaderNotificationButton = ({ notificationsPath }) => {
  const navigate = useNavigate();
  const { notificationCount } = useAuth();
  const notificationContext = useNotificationContext();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const unreadCount = Math.max(0, Number(notificationCount) || 0);
  const showUnreadBadge = unreadCount > 0;
  const needsAlertsSetup =
    FCM_WEB_PUSH_ENABLED && Boolean(notificationContext?.alertsSetup?.needsAttention);
  const startAlertsSetup = notificationContext?.alertsSetup?.startSetup;
  const isAlertsBusy = notificationContext?.alertsSetup?.isBusy;
  const markAllRead = notificationContext?.markAllRead;

  const openNotificationsInbox = () => {
    markAllRead?.mutate();
    navigate(notificationsPath);
  };

  const handleClick = (event) => {
    if (needsAlertsSetup) {
      setMenuAnchor(event.currentTarget);
      return;
    }
    openNotificationsInbox();
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleViewNotifications = () => {
    handleCloseMenu();
    openNotificationsInbox();
  };

  const handleEnableNotifications = () => {
    handleCloseMenu();
    startAlertsSetup?.();
  };

  const badgeLabel = showUnreadBadge
    ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`
    : needsAlertsSetup
      ? 'Notifications — push alerts not enabled'
      : 'Notifications';

  return (
    <>
      <Tooltip title={badgeLabel}>
        <IconButton
          onClick={handleClick}
          aria-label={badgeLabel}
          sx={{ p: 0.5, color: 'text.secondary' }}
        >
          <Badge
            badgeContent={showUnreadBadge ? unreadCount : undefined}
            max={99}
            color="primary"
            variant={showUnreadBadge ? 'standard' : 'dot'}
            invisible={!showUnreadBadge && !needsAlertsSetup}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              },
              '& .MuiBadge-dot': {
                width: 7,
                height: 7,
                minWidth: 7,
                bgcolor: 'primary.main',
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 220, mt: 0.75 } } }}
      >
        <MenuItem onClick={handleViewNotifications}>
          <ListItemIcon>
            <NotificationsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View notifications" />
        </MenuItem>
        <MenuItem onClick={handleEnableNotifications} disabled={isAlertsBusy}>
          <ListItemIcon>
            <NotificationsActiveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={isAlertsBusy ? 'Connecting…' : 'Enable push notifications'}
            secondary="Background browser alerts"
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default HeaderNotificationButton;
